import { buildFilename } from './format';
import { negotiateVideoFormat } from './codecs';
import { ExportCancelled, inspectFile, muxToBuffer } from './mux';
import { planTrim } from './timeline';
import type { Recording } from './engine';

export type ExportMode = 'fast' | 'precise';
export type ExportContainer = 'mp4' | 'mov';

export { ExportCancelled };

export interface ExportRequest {
  container: ExportContainer;
  mode: ExportMode;
  inUs: number;
  outUs: number;
  /** Output height relative to the source: 1, 0.75 or 0.5. */
  scale: number;
  /** Target bitrate for re-encodes (bits per second). */
  bitrate: number;
  includeAudio: boolean;
}

export interface ExportProgress {
  stage: 'planning' | 're-encoding' | 'muxing' | 'done';
  ratio: number;
}

export interface ExportResult {
  blob: Blob;
  filename: string;
  bytes: number;
  durationMs: number;
  container: ExportContainer;
  mode: ExportMode;
}

export interface ExportHandlers {
  onProgress?: (progress: ExportProgress) => void;
  signal?: AbortSignal;
}

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export async function exportRecording(
  recording: Recording,
  request: ExportRequest,
  handlers: ExportHandlers = {},
): Promise<ExportResult> {
  const startedAt = performance.now();
  const report = handlers.onProgress ?? (() => undefined);
  const throwIfAborted = () => {
    if (handlers.signal?.aborted) throw new ExportCancelled();
  };

  report({ stage: 'planning', ratio: 0 });
  throwIfAborted();

  const plan = planTrim({
    videoChunks: recording.video.timeline,
    keyframes: recording.keyframes,
    audioChunks: recording.audio?.timeline ?? [],
    inUs: request.inUs,
    outUs: request.outUs,
    mode: request.mode,
  });

  const useAudio = Boolean(recording.audio) && request.includeAudio;
  let videoTrack: Parameters<typeof muxToBuffer>[0]['video'];
  let audioTrack: Parameters<typeof muxToBuffer>[0]['audio'] = null;
  let outputWidth = recording.width;
  let outputHeight = recording.height;

  if (request.mode === 'precise') {
    const reencoded = await reencodeVideo(recording, request, plan, report, handlers);
    videoTrack = reencoded.video;
    outputWidth = reencoded.width;
    outputHeight = reencoded.height;
    throwIfAborted();

    // Audio stays untouched in precise mode: cutting on an AAC frame boundary
    // (~21 ms) is inaudible and avoids a second lossy generation.
    if (useAudio && recording.audio) {
      audioTrack = {
        chunks: recording.audio.chunks,
        timeline: recording.audio.timeline,
        meta: recording.audio.meta,
        startIndex: plan.audioStartIndex,
        endIndex: plan.audioEndIndex,
        originUs: plan.audioOriginUs,
        codec: recording.audio.codec,
        channels: recording.audio.channels,
        sampleRate: recording.audio.sampleRate,
      };
    }
  } else {
    videoTrack = {
      chunks: recording.video.chunks,
      timeline: recording.video.timeline,
      meta: recording.video.meta,
      startIndex: plan.videoStartIndex,
      endIndex: plan.videoEndIndex,
      originUs: plan.videoOriginUs,
    };
    if (useAudio && recording.audio) {
      audioTrack = {
        chunks: recording.audio.chunks,
        timeline: recording.audio.timeline,
        meta: recording.audio.meta,
        startIndex: plan.audioStartIndex,
        endIndex: plan.audioEndIndex,
        originUs: plan.audioOriginUs,
        codec: recording.audio.codec,
        channels: recording.audio.channels,
        sampleRate: recording.audio.sampleRate,
      };
    }
  }

  report({ stage: 'muxing', ratio: 0 });
  const buffer = await muxToBuffer({
    container: request.container,
    width: outputWidth,
    height: outputHeight,
    fps: recording.fps,
    video: videoTrack,
    audio: audioTrack,
    onProgress: (ratio) => report({ stage: 'muxing', ratio }),
    signal: handlers.signal,
  });

  const blob = new Blob([buffer], {
    type: request.container === 'mov' ? 'video/quicktime' : 'video/mp4',
  });

  // Cheap structural gate before the file reaches the user: a truncated or
  // mis-branded container is otherwise a silent failure.
  const structure = inspectFile(buffer);
  if (!structure.hasMoov || !structure.hasMdat) {
    throw new Error('The exported file is missing its media data, so nothing was written.');
  }
  if (request.container === 'mov' && structure.brand !== 'qt  ') {
    throw new Error('The QuickTime header could not be written, so no .mov was produced.');
  }
  report({ stage: 'done', ratio: 1 });

  return {
    blob,
    filename: buildFilename(request.container, outputWidth, outputHeight, recording.fps),
    bytes: blob.size,
    durationMs: performance.now() - startedAt,
    container: request.container,
    mode: request.mode,
  };
}

interface ReencodeResult {
  video: Parameters<typeof muxToBuffer>[0]['video'];
  width: number;
  height: number;
}

/**
 * Precise trim: decode from the keyframe before the in point, drop the frames
 * that precede it, and encode the remainder. Frame-accurate, at the cost of one
 * generation of re-encoding.
 */
async function reencodeVideo(
  recording: Recording,
  request: ExportRequest,
  plan: ReturnType<typeof planTrim>,
  report: (progress: ExportProgress) => void,
  handlers: ExportHandlers,
): Promise<ReencodeResult> {
  const decoderConfig = recording.video.meta?.decoderConfig;
  if (!decoderConfig?.description) {
    throw new Error('This take has no decoder configuration, so a precise trim is not possible.');
  }

  const width = Math.max(2, Math.round((recording.width * request.scale) / 2) * 2);
  const height = Math.max(2, Math.round((recording.height * request.scale) / 2) * 2);
  const scaled = width !== recording.width || height !== recording.height;
  const frameUs = recording.frameUs;

  let format = await negotiateVideoFormat(width, height, recording.fps, 'balanced', request.bitrate);
  // Reuse the source codec string when it still validates; it keeps the export
  // consistent with the capture settings.
  try {
    const support = await VideoEncoder.isConfigSupported({
      codec: recording.videoCodec || format.codec,
      width,
      height,
      bitrate: request.bitrate,
      framerate: recording.fps,
      latencyMode: 'realtime',
      hardwareAcceleration: 'no-preference',
      avc: { format: 'avc' },
    });
    if (support.supported) format = { ...format, codec: recording.videoCodec || format.codec };
  } catch {
    // Keep the negotiated format.
  }

  const outChunks: EncodedVideoChunk[] = [];
  const outTimeline: Array<{ timestamp: number; duration: number; type: 'key' | 'delta' }> = [];
  let outMeta: EncodedVideoChunkMetadata | null = null;
  let lastOutTs = -1;
  let pipelineError: unknown = null;
  const encoder = new VideoEncoder({
    output: (chunk, meta) => {
      if (!outMeta && meta?.decoderConfig?.description) outMeta = meta;
      let timestamp = chunk.timestamp;
      if (!Number.isFinite(timestamp) || timestamp <= lastOutTs) timestamp = lastOutTs + frameUs;
      lastOutTs = timestamp;
      outChunks.push(chunk);
      outTimeline.push({ timestamp, duration: frameUs, type: chunk.type });
    },
    error: (error) => {
      pipelineError = error;
    },
  });
  encoder.configure({
    codec: format.codec,
    width,
    height,
    bitrate: request.bitrate,
    framerate: recording.fps,
    // Realtime keeps the encoder free of B-frames, so each output chunk's
    // timestamp stays in decode order and maps 1:1 onto our timeline.
    latencyMode: 'realtime',
    hardwareAcceleration: 'no-preference',
    avc: { format: 'avc' },
  });

  const canvas = scaled ? document.createElement('canvas') : null;
  if (canvas) {
    canvas.width = width;
    canvas.height = height;
  }
  const ctx = canvas?.getContext('2d', { alpha: false }) ?? null;
  if (ctx) {
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
  }

  const originUs = plan.videoOriginUs;
  const endUs = request.outUs;
  let firstFrame = true;

  const decoder = new VideoDecoder({
    output: (frame) => {
      try {
        if (pipelineError) return;
        const deltaUs = frame.timestamp - originUs;
        if (deltaUs < 0) return;
        const timestamp = Math.round(deltaUs / frameUs) * frameUs;
        if (timestamp > endUs - originUs) return;

        if (canvas && ctx) {
          ctx.drawImage(frame, 0, 0, width, height);
          const scaledFrame = new VideoFrame(canvas, { timestamp, duration: frameUs });
          encoder.encode(scaledFrame, { keyFrame: firstFrame });
          scaledFrame.close();
        } else {
          const wrapped = new VideoFrame(frame, { timestamp, duration: frameUs });
          encoder.encode(wrapped, { keyFrame: firstFrame });
          wrapped.close();
        }
        firstFrame = false;
      } catch (error) {
        pipelineError = error;
      } finally {
        frame.close();
      }
    },
    error: (error) => {
      pipelineError = error;
    },
  });

  try {
    decoder.configure({
      codec: decoderConfig.codec,
      description: decoderConfig.description,
      codedWidth: decoderConfig.codedWidth ?? recording.width,
      codedHeight: decoderConfig.codedHeight ?? recording.height,
      hardwareAcceleration: 'no-preference',
    });

    const total = Math.max(1, plan.videoEndIndex - plan.videoStartIndex);
    for (let i = plan.videoStartIndex; i < plan.videoEndIndex; i += 1) {
      if (handlers.signal?.aborted) throw new ExportCancelled();
      const chunk = recording.video.chunks[i];
      if (recording.video.timeline[i].timestamp > endUs) break;

      while (decoder.decodeQueueSize > 8) await delay(2);
      while (encoder.encodeQueueSize > 8) await delay(2);
      if (pipelineError) break;
      decoder.decode(chunk);

      if ((i - plan.videoStartIndex) % 12 === 0) {
        const ratio = (i - plan.videoStartIndex) / total;
        report({ stage: 're-encoding', ratio: Math.min(0.98, ratio) });
        await delay(0);
      }
    }
    await decoder.flush();
    await encoder.flush();
  } finally {
    try {
      decoder.close();
    } catch {
      // already closed
    }
    try {
      encoder.close();
    } catch {
      // already closed
    }
  }

  if (pipelineError) throw pipelineError;
  if (outChunks.length === 0) {
    throw new Error('The precise trim produced no frames. Try moving the handles.');
  }
  report({ stage: 're-encoding', ratio: 1 });

  return {
    width,
    height,
    video: {
      chunks: outChunks,
      timeline: outTimeline,
      meta: outMeta,
      startIndex: 0,
      endIndex: outChunks.length,
      originUs: 0,
    },
  };
}

/** Rough output size for the export panel, before anything is written. */
export function estimateExportBytes(
  recording: Recording,
  request: Pick<ExportRequest, 'mode' | 'bitrate' | 'scale' | 'includeAudio'>,
  durationUs: number,
): number {
  if (request.mode === 'fast') {
    const ratio = recording.durationUs > 0 ? durationUs / recording.durationUs : 1;
    return recording.bytes * ratio;
  }
  const videoBits = (request.bitrate * durationUs) / 1_000_000;
  const audioBitrate = request.includeAudio && recording.audio ? 160_000 : 0;
  const audioBits = (audioBitrate * durationUs) / 1_000_000;
  return (videoBits + audioBits) / 8;
}
